import React, { useCallback, useState } from 'react';
import { Box, TextField, Tooltip, type TextFieldProps } from '@mui/material';
import { NOTE_NEON } from './notePreviewUi';

/** User marker tags in plain text, e.g. ppp7 or pppGinMotion in "ppp7GinMotion". */
export const TEXT_MARKER_REGEX = /ppp\w+/gi;

export type TextMarkerRenderContext = 'table' | 'form' | 'form-backdrop';

const textMarkerNeonBaseSx = {
    display: 'inline',
    color: 'inherit',
    fontSize: 'inherit',
    lineHeight: 1.2,
    px: 0.5,
    py: 0.15,
    mx: 0.05,
    borderRadius: '7px',
} as const;

const textMarkerFormBreatheKeyframes = {
    '@keyframes textMarkerBreatheStrong': {
        '0%, 100%': {
            boxShadow:
                '0 0 4px rgba(34, 211, 238, 0.28), 0 0 8px rgba(129, 140, 248, 0.12), 0 0 16px rgba(232, 121, 249, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.22)',
            outlineColor: 'rgba(34, 211, 238, 0.28)',
            textShadow: '0 0 2px rgba(34, 211, 238, 0.3), 0 0 4px rgba(232, 121, 249, 0.1)',
        },
        '50%': {
            boxShadow:
                '0 0 22px rgba(34, 211, 238, 1), 0 0 44px rgba(129, 140, 248, 0.92), 0 0 70px rgba(232, 121, 249, 0.74), inset 0 1px 0 rgba(255, 255, 255, 0.42)',
            outlineColor: 'rgba(34, 211, 238, 1)',
            textShadow: '0 0 18px rgba(34, 211, 238, 1), 0 0 36px rgba(232, 121, 249, 1)',
        },
    },
} as const;

const textMarkerFormBackdropBreatheKeyframes = {
    '@keyframes textMarkerBreatheBackdrop': {
        '0%, 100%': {
            opacity: 0.5,
            outlineColor: 'rgba(34, 211, 238, 0.28)',
            boxShadow:
                'inset 0 0 6px rgba(34, 211, 238, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
        },
        '50%': {
            opacity: 1,
            outlineColor: 'rgba(34, 211, 238, 0.95)',
            boxShadow:
                'inset 0 0 16px rgba(34, 211, 238, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.38)',
        },
    },
} as const;

const getTextMarkerSx = (context: TextMarkerRenderContext) => {
    if (context === 'table') {
        return {
            ...textMarkerNeonBaseSx,
            background: NOTE_NEON.gradientSoft,
            outline: '1px solid rgba(34, 211, 238, 0.35)',
            outlineOffset: 0,
            boxShadow: 'none',
            textShadow: 'none',
            whiteSpace: 'nowrap' as const,
        };
    }

    if (context === 'form-backdrop') {
        return {
            ...textMarkerNeonBaseSx,
            color: 'transparent',
            background: NOTE_NEON.gradient,
            outline: '1.5px solid rgba(34, 211, 238, 0.55)',
            outlineOffset: 0,
            textShadow: 'none',
            ...textMarkerFormBackdropBreatheKeyframes,
            animation: 'textMarkerBreatheBackdrop 5s ease-in-out infinite',
        };
    }

    return {
        ...textMarkerNeonBaseSx,
        background: NOTE_NEON.gradient,
        boxShadow:
            '0 0 12px rgba(34, 211, 238, 0.72), 0 0 26px rgba(129, 140, 248, 0.55), 0 0 44px rgba(232, 121, 249, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.32)',
        outline: '1.5px solid rgba(34, 211, 238, 0.65)',
        outlineOffset: '2px',
        textShadow: '0 0 10px rgba(34, 211, 238, 0.95), 0 0 18px rgba(232, 121, 249, 0.75)',
        ...textMarkerFormBreatheKeyframes,
        animation: 'textMarkerBreatheStrong 5s ease-in-out infinite',
    };
};

export const hasTextMarkers = (text: string): boolean => {
    TEXT_MARKER_REGEX.lastIndex = 0;
    return TEXT_MARKER_REGEX.test(text);
};

export const renderTextWithMarkers = (
    text: string,
    context: TextMarkerRenderContext = 'form',
): React.ReactNode => {
    if (!text) {
        return text;
    }

    TEXT_MARKER_REGEX.lastIndex = 0;
    if (!TEXT_MARKER_REGEX.test(text)) {
        return text;
    }

    const re = new RegExp(TEXT_MARKER_REGEX.source, 'gi');
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let key = 0;
    const isBackdrop = context === 'form-backdrop';

    while ((match = re.exec(text)) !== null) {
        if (match.index > lastIndex) {
            const chunk = text.slice(lastIndex, match.index);
            parts.push(
                <Box
                    component="span"
                    key={`t-${key}`}
                    sx={isBackdrop ? { color: 'transparent' } : undefined}
                >
                    {chunk}
                </Box>,
            );
            key += 1;
        }

        parts.push(
            <Box component="mark" key={`m-${key}`} sx={getTextMarkerSx(context)}>
                {match[0]}
            </Box>,
        );
        key += 1;
        lastIndex = re.lastIndex;
    }

    if (lastIndex < text.length) {
        parts.push(
            <Box
                component="span"
                key={`t-${key}`}
                sx={isBackdrop ? { color: 'transparent' } : undefined}
            >
                {text.slice(lastIndex)}
            </Box>,
        );
    }

    return <>{parts}</>;
};

type MarkerTableTextProps = {
    text: string;
    emptyFallback?: React.ReactNode;
};

/** Table cell text: soft marker highlight, nowrap + ellipsis, full value in tooltip. */
export const MarkerTableText: React.FC<MarkerTableTextProps> = ({
    text,
    emptyFallback = '—',
}) => {
    const value = (text ?? '').trim();
    if (!value) {
        return <>{emptyFallback}</>;
    }

    const content = hasTextMarkers(value) ? renderTextWithMarkers(value, 'table') : value;

    return (
        <Tooltip title={value}>
            <Box
                component="span"
                sx={{
                    display: 'block',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {content}
            </Box>
        </Tooltip>
    );
};

const getBackdropInsets = (
    size: TextFieldProps['size'],
    multiline: boolean | undefined,
    hasLabel: boolean,
) => {
    const isSmall = size === 'small';
    if (multiline) {
        return {
            fontSize: isSmall ? '0.875rem' : '1rem',
            lineHeight: 1.4375,
            paddingTop: hasLabel ? (isSmall ? '20px' : '22px') : isSmall ? '8.5px' : '16.5px',
            paddingRight: '14px',
            paddingBottom: isSmall ? '8.5px' : '16.5px',
            paddingLeft: '14px',
        };
    }

    return {
        fontSize: isSmall ? '0.875rem' : '1rem',
        lineHeight: 1.4375,
        paddingTop: hasLabel ? (isSmall ? '12.5px' : '16.5px') : isSmall ? '8.5px' : '16.5px',
        paddingRight: '14px',
        paddingBottom: isSmall ? '8.5px' : '16.5px',
        paddingLeft: '14px',
    };
};

export const MarkerHighlightTextField: React.FC<TextFieldProps> = ({
    value,
    sx,
    InputProps,
    inputProps,
    multiline,
    size,
    label,
    inputRef: externalInputRef,
    ...rest
}) => {
    const text = String(value ?? '');
    const showMarkers = hasTextMarkers(text);
    const [scrollLeft, setScrollLeft] = useState(0);

    const handleInputScroll = useCallback((event: React.UIEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setScrollLeft(event.currentTarget.scrollLeft);
    }, []);

    const mergedInputProps = {
        ...inputProps,
        onScroll: (event: React.UIEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            handleInputScroll(event);
            inputProps?.onScroll?.(event);
        },
    };

    const mergedInputRef = useCallback(
        (node: HTMLInputElement | HTMLTextAreaElement | null) => {
            if (typeof externalInputRef === 'function') {
                externalInputRef(node);
            } else if (externalInputRef && typeof externalInputRef === 'object') {
                (externalInputRef as React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>).current = node;
            }
        },
        [externalInputRef],
    );

    if (!showMarkers) {
        return (
            <TextField
                value={value}
                sx={sx}
                InputProps={InputProps}
                inputProps={inputProps}
                multiline={multiline}
                size={size}
                label={label}
                inputRef={externalInputRef}
                {...rest}
            />
        );
    }

    const backdropInsets = getBackdropInsets(size, multiline, Boolean(label));

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            <Box
                aria-hidden
                sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 0,
                    pointerEvents: 'none',
                    overflow: 'hidden',
                    borderRadius: 'inherit',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        overflow: 'hidden',
                        ...backdropInsets,
                        fontFamily: 'inherit',
                        letterSpacing: 'inherit',
                        whiteSpace: multiline ? 'pre-wrap' : 'pre',
                        wordBreak: multiline ? 'break-word' : 'normal',
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-block',
                            minWidth: '100%',
                            transform: `translateX(-${scrollLeft}px)`,
                        }}
                    >
                        {renderTextWithMarkers(text, 'form-backdrop')}
                    </Box>
                </Box>
            </Box>
            <TextField
                {...rest}
                label={label}
                multiline={multiline}
                size={size}
                value={value}
                InputProps={InputProps}
                inputProps={mergedInputProps}
                inputRef={mergedInputRef}
                sx={{
                    ...sx,
                    position: 'relative',
                    zIndex: 1,
                    '& .MuiInputBase-root': {
                        backgroundColor: 'transparent',
                    },
                    '& .MuiInputBase-input': {
                        backgroundColor: 'transparent',
                    },
                }}
            />
        </Box>
    );
};
